// Single source of truth for combining the price layers (base variant + bracket
// addon + rip cut) into a final total. Both this element and addon-bracket-selector.js
// read/write the same globals (window.ripCutState, window.bracketAddonState,
// data-base-price) but must never each implement their own copy of this formula —
// divergence between the two was how the displayed price could drift from reality.
window.ScaffPricing = window.ScaffPricing || {
  totalCents({ baseCents = 0, addonCents = 0, ripCutCents = 0 } = {}) {
    return (baseCents || 0) + (addonCents || 0) + (ripCutCents || 0);
  },

  // Tracks whether a variant re-match triggered by a slider move is still in
  // flight — a network round trip for Length (via the theme's section API) or
  // the width-tier product match/swap for Width. While anything is pending,
  // Add to Cart is disabled so a customer can never submit a stale variant:
  // the "Customer Selected …" line item properties update instantly on every
  // slider tick, but the real `id` input (what actually gets added/priced)
  // only updates once the async match resolves, so submitting mid-flight adds
  // whatever size was selected *before* the customer's latest drag.
  _pending: {},
  _pendingTimers: {},
  _pendingWatcherInitialized: false,

  _initPendingWatcher() {
    if (this._pendingWatcherInitialized) return;
    this._pendingWatcherInitialized = true;
    // Fires once the theme's section-rendering navigation (used by the Length
    // slider's variant swap) has finished morphing the DOM back in.
    window.addEventListener('theme:section:update', () => this.setPending('length', false));
  },

  setPending(key, isPending) {
    this._initPendingWatcher();
    clearTimeout(this._pendingTimers[key]);
    if (isPending) {
      this._pending[key] = true;
      // Safety net: never leave the button stuck disabled if the expected
      // completion signal doesn't arrive (failed fetch, renamed event, etc).
      this._pendingTimers[key] = setTimeout(() => this.setPending(key, false), 5000);
    } else {
      delete this._pending[key];
    }
    this._applyPendingState();
  },

  isPending() {
    return Object.keys(this._pending).length > 0;
  },

  _applyPendingState() {
    const pending = this.isPending();
    document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      if (pending) {
        if (!btn.disabled) {
          btn.dataset.scaffPendingDisabled = 'true';
          btn.disabled = true;
        }
      } else if (btn.dataset.scaffPendingDisabled === 'true') {
        delete btn.dataset.scaffPendingDisabled;
        btn.disabled = false;
      }
    });
  }
};

class OptionWidthPrimeRange extends HTMLElement {
    // Cache for fetched product HTML by URL
    static productHtmlCache = {};
  constructor() {
    super();
    this.selectors = {
      slider: '[data-width-prime-slider]',
      selectedValue: '[data-selected-value]',
      exactInput: '[data-exact-dimension-input]',
      container: '.length-range',
      knob: '[data-knob-value]',
      json: 'script[type="application/json"]',
      variantsBlock: '.product__block__variants'
    };
    this.is_first = true;
    this._updatingRipCutPrice = false;
    this._boundRipCutChange = this.handleRipCutDependencyChange.bind(this);
    this._boundSliderChange = this.handleSliderChange.bind(this);
  }

  isPriceDebugEnabled() {
    if (window.WIDTH_PRIME_PRICE_DEBUG === true) return true;
    if (this.hasAttribute('data-price-debug')) return true;
    try {
      return new URLSearchParams(window.location.search).has('price_debug');
    } catch {
      return false;
    }
  }

  formatCents(cents) {
    const currencyFormatter = window.themeVendor?.themeCurrency;
    if (currencyFormatter && window.theme) {
      return currencyFormatter.formatMoney(cents, theme.moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  getSelectedOptionValues() {
    const variantBlock = document.querySelector(this.selectors.variantsBlock);
    if (!variantBlock) return [];

    const optionElements = Array.from(variantBlock.querySelectorAll('[data-option-position]'));
    optionElements.sort((a, b) =>
      parseInt(a.getAttribute('data-option-position'), 10) - parseInt(b.getAttribute('data-option-position'), 10)
    );

    return optionElements.map(optionEl => {
      const hiddenInput = optionEl.querySelector('input[type="hidden"]');
      if (hiddenInput?.value) return hiddenInput.value;

      const checkedRadio = optionEl.querySelector('input[type="radio"]:checked');
      if (checkedRadio) return checkedRadio.value;

      const checkedSwatch = optionEl.querySelector('[swatch-option-selector]:checked, [data-addon-input]:checked');
      if (checkedSwatch) return checkedSwatch.value;

      const activeLink = optionEl.querySelector('a.active[data-value], a[data-value].active');
      if (activeLink) return activeLink.getAttribute('data-value');

      return null;
    });
  }

  getVariantBreakdown() {
    const priceEl = document.querySelector('[data-product-price]');
    const variantInput = document.querySelector("input[type='hidden'][name='id']");
    const rawBase = priceEl?.getAttribute('data-base-price');
    const cents = rawBase !== null ? (parseInt(rawBase, 10) || 0) : null;
    const options = this.getSelectedOptionValues().filter(Boolean);
    const variantId = variantInput?.value || window.currentMatchingVariant?.id || null;

    let detail = variantId ? `variant #${variantId}` : 'variant (id pending)';
    if (options.length) detail += ` — ${options.join(' / ')}`;

    const widthTier = window.currentWidthProduct?.product_metafields?.width;
    if (widthTier) detail += ` @ ${widthTier}mm width tier`;

    return { cents, detail, label: 'VARIANT' };
  }

  getBracketAddonBreakdown() {
    const el = document.querySelector('addon-bracket-selector');
    if (!el) return { cents: 0, detail: 'not on this product', label: 'ADDON' };

    const state = window.bracketAddonState;
    if (!state) return { cents: 0, detail: 'bracket add-on (loading)', label: 'ADDON' };
    if (!state.enabled) return { cents: 0, detail: 'bracket add-on off', label: 'ADDON' };
    if (!state.totalCents) return { cents: 0, detail: 'bracket add-on on — £0', label: 'ADDON' };

    const title = state.title || 'bracket';
    const detail = `${state.quantity} × ${this.formatCents(state.unitCents)} — ${title}${state.variantId ? ` (#${state.variantId})` : ''}`;
    return { cents: state.totalCents, detail, label: 'ADDON' };
  }

  getRipCutBreakdown() {
    const rip = window.ripCutState || {};
    const widthMm = parseInt(this.slider?.value, 10);

    if (!rip.active) {
      const reason = !rip.id
        ? 'no rip-cut product'
        : `width ${widthMm}mm ≥ 225mm`;
      return { cents: 0, detail: `off (${reason})`, label: 'RIP CUT' };
    }

    const detail = `${rip.quantity} ft × ${this.formatCents(rip.unitPriceCents)}${rip.id ? ` (variant #${rip.id})` : ''}`;
    return { cents: rip.totalPriceCents || 0, detail, label: 'RIP CUT' };
  }

  getPriceBreakdown() {
    const variant = this.getVariantBreakdown();
    const addon = this.getBracketAddonBreakdown();
    const ripCut = this.getRipCutBreakdown();
    const priceEl = document.querySelector('[data-product-price]');
    const displayedCents = priceEl ? this.parseDisplayedMoney(priceEl.textContent) : NaN;

    const variantCents = variant.cents ?? 0;
    const addonCents = addon.cents || 0;
    const ripCents = ripCut.cents || 0;
    const sumCents = variantCents + addonCents + ripCents;

    return {
      variant,
      addon,
      ripCut,
      total: {
        cents: Number.isFinite(displayedCents) ? displayedCents : sumCents,
        sumCents,
        detail: Number.isFinite(displayedCents) && displayedCents !== sumCents
          ? `display differs from sum by ${this.formatCents(Math.abs(displayedCents - sumCents))} (theme still updating?)`
          : 'variant + addon + rip cut'
      }
    };
  }

  schedulePriceSummaryLog(trigger, delay = 150) {
    if (!this.isPriceDebugEnabled()) return;
    clearTimeout(this._priceSummaryTimer);
    this._priceSummaryTimer = setTimeout(() => this.logPriceSummary(trigger), delay);
  }

  logPriceSummary(trigger) {
    if (!this.isPriceDebugEnabled()) return;

    const widthMm = parseInt(this.slider?.value, 10);
    const lengthSlider = document.querySelector('[data-option-range-slider]');
    const lengthCm = parseFloat(lengthSlider?.value);
    const { variant, addon, ripCut, total } = this.getPriceBreakdown();

    const row = (item) => {
      const amount = item.cents !== null && item.cents !== undefined
        ? this.formatCents(item.cents)
        : '—';
      return { LAYER: item.label, AMOUNT: amount, DETAIL: item.detail };
    };

    console.log(
      `%c[PRICE CHANGE] ${trigger}`,
      'font-size:13px;font-weight:700;color:#1d4ed8;background:#eff6ff;padding:2px 6px;border-radius:3px',
      `(width ${widthMm}mm · length ${Number.isFinite(lengthCm) ? lengthCm + 'cm' : '?'})`
    );
    console.table([
      row(variant),
      row(addon),
      row(ripCut),
      { LAYER: 'TOTAL', AMOUNT: this.formatCents(total.cents), DETAIL: total.detail }
    ]);
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.slider = this.querySelector(this.selectors.slider);
    this.selectedValueDisplay = this.querySelector(this.selectors.selectedValue);
    this.exactInput = this.querySelector(this.selectors.exactInput);
    this.container = this.querySelector(this.selectors.container);
    this.knob = this.querySelector(this.selectors.knob);
    this.json = this.querySelector(this.selectors.json);
    if (this.json) { this.jsonData = JSON.parse(this.json.textContent); }

    if (!this.container || !this.slider) return;

    this.setupSlider();
    if (this.exactInput) {
      // Sync to the slider's actual bounds (not a fixed number) since this
      // varies per product — e.g. some width families max out at 440mm,
      // others at 660mm.
      this.exactInput.min = this.slider.min;
      this.exactInput.max = this.slider.max;
      this.exactInput.addEventListener('input', this.handleExactInputChange.bind(this));
      this.exactInput.addEventListener('blur', this.handleExactInputBlur.bind(this));
    }
    this.findOrCreateLineItemInput();
    this.setupEventListeners();
    document.addEventListener('length-changed', this._boundRipCutChange);
    document.addEventListener('theme:variant:change', this._boundRipCutChange);
    document.addEventListener('variants-block:updated', this._boundRipCutChange);
    this._boundBracketChange = () => this.schedulePriceSummaryLog('addon change', 120);
    document.addEventListener('bracket-addon:change', this._boundBracketChange);
    this.observeProductUpdates();
    this.updateInitialDisplay();
    this.schedulePriceSummaryLog('page load', 900);
    this.startWidthReconciler();
  }

  disconnectedCallback() {
    document.removeEventListener('length-changed', this._boundRipCutChange);
    document.removeEventListener('theme:variant:change', this._boundRipCutChange);
    document.removeEventListener('variants-block:updated', this._boundRipCutChange);
    document.removeEventListener('bracket-addon:change', this._boundBracketChange);
    if (this._productObserver) this._productObserver.disconnect();
    if (this._widthVariantSyncTimer) clearTimeout(this._widthVariantSyncTimer);
    if (this._variantDebounceTimer) clearTimeout(this._variantDebounceTimer);
    if (this._priceSummaryTimer) clearTimeout(this._priceSummaryTimer);
    if (this._widthReconcilerInterval) clearInterval(this._widthReconcilerInterval);
  }

  // Safety net: every 2s, check whether the URL genuinely matches what the
  // width slider is currently set to, and self-correct if not — this catches
  // drift from any cause (a missed event, a future edge case in the matching
  // logic, etc.) without needing every event-driven path to be perfect.
  // Deliberately reuses handleVariantChange (the single source of truth for
  // matching) rather than any separate correction logic of its own.
  startWidthReconciler() {
    if (this._widthReconcilerInterval) return;
    this._widthReconcilerInterval = setInterval(() => {
      this.reconcileWidthState();
    }, 2000);
  }

  reconcileWidthState() {
    if (!this.slider || !this.jsonData?.length) return;
    // An update is already in flight (e.g. the user is actively dragging, or
    // a match from a moment ago hasn't resolved yet) — let it finish rather
    // than racing it with a second, overlapping correction attempt.
    if (window.ScaffPricing.isPending()) return;

    const widthValue = parseInt(this.slider.value, 10);
    if (!Number.isFinite(widthValue)) return;

    const expectedProduct = this.findNearestWidthProduct(widthValue);
    const expectedUrl = expectedProduct?.variants?.[0]?.url;
    if (!expectedUrl) return;

    const expectedPath = expectedUrl.split('?')[0];
    const inSync = window.location.pathname === expectedPath;

    if (this.isPriceDebugEnabled()) {
      console.log(
        '%c[Width Reconciler] check',
        'font-size:11px;font-weight:600;color:#7c3aed;background:#f5f3ff;padding:2px 6px;border-radius:3px',
        { sliderWidth: widthValue, expectedPath, currentPath: window.location.pathname, inSync }
      );
    }

    if (inSync) return; // already correct

    console.warn('[Width Prime] Reconciler: URL out of sync with slider — correcting.', {
      sliderWidth: widthValue,
      expectedPath,
      currentPath: window.location.pathname
    });

    window.currentWidthProduct = expectedProduct;
    window.ScaffPricing.setPending('width', true);
    this.handleVariantChange(expectedProduct, window.currentWidthDomProduct);
  }

  setupSlider() {
    const min = parseFloat(this.slider.getAttribute('min')) || 5;
    const max = parseFloat(this.slider.getAttribute('max')) || 250;
    const step = parseFloat(this.slider.getAttribute('step')) || 1;
    this.slider.min = min;
    this.slider.max = max;
    this.slider.step = step;

    const value = parseFloat(this.slider.value);
    if (!Number.isFinite(value) || value < min) {
      this.slider.value = min;
    } else if (value > max) {
      this.slider.value = max;
    }
  }

  findOrCreateLineItemInput() {
    const productForm = this.closest('.product-section')?.querySelector('form[action^="/cart/add"][data-product-form]');

    if (!productForm) return;

    this.lineItemInput = productForm.querySelector('input[data-width-property]');

    if (!this.lineItemInput) { this.lineItemInput = productForm.querySelector('input[name="properties[Customer Selected Width]"]'); }

    if (!this.lineItemInput) {
      this.lineItemInput = document.createElement('input');
      this.lineItemInput.type = 'hidden';
      this.lineItemInput.name = 'properties[Customer Selected Width]';
      this.lineItemInput.setAttribute('data-width-property', '');
      productForm.appendChild(this.lineItemInput);
    }
  }

  updateLineItemProperty(value) {
    if (!this.lineItemInput) return;

    const numericValue = parseFloat(value);
    const inches = (numericValue / 25.4).toFixed(1);
    this.lineItemInput.value = `${numericValue}mm (approx ${inches}in)`;
  }

  setupEventListeners() {
    this.slider.removeEventListener('input', this._boundSliderChange);
    this.slider.addEventListener('input', this._boundSliderChange);
    this.initializeRipCutFromSlider();
  }

  initializeRipCutFromSlider() {
    const widthValue = parseInt(this.slider.value, 10);

    if (this.jsonData?.length > 0) {
      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        window.currentWidthProduct = nearestProduct;
        // On page load the DOM is server-rendered for exactly this product,
        // so the "what does the DOM actually show" tracker starts in sync.
        window.currentWidthDomProduct = nearestProduct;
        this.applyRipCutMetadata(nearestProduct);
      }
    }

    // Rip cut math is pure arithmetic on data we already have — no need to
    // gate it behind a delay while other layers "settle".
    this.updateRipCutState(widthValue, true);

    this.dispatchEvent(new CustomEvent('width-prime-changed', {
      detail: { value: widthValue },
      bubbles: true
    }));
    this.is_first = false;
  }

  updateInitialDisplay() {
    const currentValue = parseInt(this.slider.value, 10);

    requestAnimationFrame(() => {
      this.updateKnob(currentValue);
      this.updateSelectedValueDisplay(currentValue);
      this.updateLineItemProperty(currentValue);
    });
  }

  handleExactInputChange(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) return;

    // Hard ceiling: never let the box hold a value past the slider's max,
    // even mid-typing — clamp immediately rather than waiting for blur.
    if (val > max) {
      val = max;
      event.target.value = val;
    }

    // Below min is left alone while typing (e.g. typing "3" en route to
    // "30") — handleExactInputBlur clamps that once they finish.
    if (val >= min && val <= max) {
      this.slider.value = val;
      this.handleSliderChange({ target: this.slider });
    }
  }

  handleExactInputBlur(event) {
    let val = parseFloat(event.target.value);
    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);

    if (isNaN(val)) {
      val = min;
    } else if (val < min) {
      val = min;
    } else if (val > max) {
      val = max;
    }

    event.target.value = val;
    this.slider.value = val;
    this.handleSliderChange({ target: this.slider });
  }

  handleSliderChange(event) {
    const widthValue = parseInt(event.target.value, 10);

    if (this.exactInput && parseInt(this.exactInput.value, 10) !== widthValue) {
      this.exactInput.value = widthValue;
    }

    this.updateKnob(widthValue);
    this.updateLineItemProperty(widthValue);
    this.updateSelectedValueDisplay(widthValue);

    // Use JSON data if available (for variant matching, title updates, etc.)
    if (this.jsonData && this.jsonData.length > 0) {
      // Use the DOM-reflected tracker, not window.currentWidthProduct — the
      // latter is overwritten on every tick to record the *target* tier, but
      // the actual .product__block__variants markup (and thus the Style/Oil
      // label text getSelectedOptionValues() reads) only catches up once the
      // debounced swap below actually completes. During a fast continuous
      // drag, several ticks can land on the same target tier before that
      // swap ever fires — using window.currentWidthProduct here would then
      // hand mapSelectedValuesToProduct the *same* product as both source
      // and destination, so it can't find where "Flamed" (still showing,
      // from the untouched DOM) maps to on a product whose own value is
      // "Flame" — the map silently fails and the match comes back empty.
      const fromProduct = window.currentWidthDomProduct;
      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        window.currentWidthProduct = nearestProduct;
        this.applyRipCutMetadata(nearestProduct);
        // Update product title if available
        if (nearestProduct.product_metafields?.title) {
          const titleEl = document.querySelector('.product__title');
          if (titleEl) {
            titleEl.textContent = nearestProduct.product_metafields.title;
          }
        }

        // The matched variant's price is already sitting in jsonData — no
        // network round trip required to know it. Sync it into data-base-price
        // immediately so every slider movement prices correctly right away,
        // instead of waiting on the (still-useful, but comparatively slow)
        // full HTML swap below. That swap only replaces cosmetic DOM
        // (variant options markup, description, images) and stays debounced.
        // Skip while Length's own click is still in flight — the DOM would
        // hand back a stale Size and sync the wrong base price.
        const immediateVariant = this.isLengthPending()
          ? null
          : this.findVariantMatch(nearestProduct, fromProduct);
        if (immediateVariant) {
          window.currentMatchingVariant = immediateVariant;
          this.syncVariantBasePrice(immediateVariant);
        }

        if (this.is_first === false) {
          // A heavier product/variant swap is now queued — block Add to Cart
          // until it actually resolves (handleVariantChange clears this),
          // even though the price itself was already synced above.
          window.ScaffPricing.setPending('width', true);
          this.debounceVariantChange(nearestProduct, fromProduct, 650);
        }
      }
    }

    // Rip cut depends on the width tier and length we just resolved above —
    // recompute and repaint the price synchronously so it can never lag
    // behind (or be starved by) slider movement.
    this.updateRipCutState(widthValue, true);

    this.schedulePriceSummaryLog('width change', 50);

    this.dispatchEvent(new CustomEvent('width-prime-changed', {
      detail: { value: widthValue },
      bubbles: true
    }));
    this.is_first = false;
  }

  // Pure lookup: matches the currently-selected product options (Length, Style,
  // Oil, etc.) against a width-tier product's variant list. Performs no DOM
  // mutation or network I/O so it is safe to call on every slider 'input' event.
  // Returns null (not a fallback) when options aren't resolvable yet, so callers
  // can fall back to the debounced/retrying handleVariantChange path instead.
  //
  // `fromProduct` — the width-tier product the current DOM selection was
  // rendered from, if known — lets us bridge sibling products whose merchant
  // gave the *same* option slot slightly different label text (e.g. "Flame" on
  // one width tier vs "Flamed" on another). Without this, a label mismatch
  // makes the exact match fail silently, and picking the wrong Style/Oil
  // variant is how a bigger size can end up looking cheaper.
  findVariantMatch(product, fromProduct = null) {
    if (!product?.variants) return null;

    const selectedValues = this.getSelectedOptionValues();
    if (!selectedValues.length || selectedValues.includes(null)) return null;

    const mapped = this.mapSelectedValuesToProduct(selectedValues, fromProduct, product);
    return product.variants.find(variant =>
      variant.options.every((option, index) => option === mapped[index])
    ) || null;
  }

  normalizeOptionValue(value) {
    return String(value ?? '').trim().toLowerCase();
  }

  // The Length exact-input box resolves its real variant click on a 300ms
  // debounce (see option-length-range.js). Reading getSelectedOptionValues()
  // before that lands returns the *previous* Size, so a width-tier match
  // computed in that window silently locks the fetched product HTML (and
  // every Sanding/Oil link inside it) to the wrong length. Callers that are
  // about to read selected options for matching must wait this out first.
  isLengthPending() {
    return Boolean(window.ScaffPricing?._pending?.length);
  }

  // Ordered, de-duplicated list of the values available at a given option
  // position for a product, built straight from its own variant list (in
  // first-seen order) rather than trusting any particular label text.
  getOptionValuesAtPosition(product, position) {
    const seen = [];
    for (const variant of product?.variants || []) {
      const val = variant.options?.[position];
      if (val !== undefined && val !== null && !seen.includes(val)) seen.push(val);
    }
    return seen;
  }

  // For each selected option, keep it as-is if `toProduct` has the same value
  // (case/whitespace-insensitive). Otherwise, find which "slot" that value
  // occupies among `fromProduct`'s own options at that position, and use
  // whatever sits in the same slot on `toProduct` — this survives sibling
  // products labelling the same option differently, as long as the merchant
  // kept the option *values* in the same relative order across the family.
  mapSelectedValuesToProduct(selectedValues, fromProduct, toProduct) {
    return selectedValues.map((value, position) => {
      if (value == null) return value;

      const toValues = this.getOptionValuesAtPosition(toProduct, position);
      const normalizedValue = this.normalizeOptionValue(value);
      if (toValues.some(v => this.normalizeOptionValue(v) === normalizedValue)) return value;

      if (!fromProduct) return value;
      const fromValues = this.getOptionValuesAtPosition(fromProduct, position);
      const index = fromValues.findIndex(v => this.normalizeOptionValue(v) === normalizedValue);
      if (index === -1 || !toValues[index]) return value;

      return toValues[index];
    });
  }

  applyRipCutMetadata(product) {
    const cutId = product?.product_metafields?.cut;
    const cutPrice = product?.product_metafields?.cut_price;

    if (cutId) {
      this.slider.setAttribute('data-variant-cut', cutId);
      this.slider.setAttribute('data-variant-cut-price', Number(cutPrice) || 0);
    } else {
      this.slider.removeAttribute('data-variant-cut');
      this.slider.removeAttribute('data-variant-cut-price');
    }
  }

  getRipCutQuantity() {
    const lengthSlider = document.querySelector('[data-option-range-slider]');
    const lengthCm = parseFloat(lengthSlider?.value);
    if (!Number.isFinite(lengthCm) || lengthCm <= 0) return 1;

    // Rip cut is sold per foot. Convert the selected centimetres to feet and
    // always round upward (for example, 5.5ft becomes 6 units).
    return Math.max(1, Math.ceil(lengthCm / 30.48));
  }

  updateRipCutState(widthValue = parseFloat(this.slider?.value), applyPrice = true) {
    const hasRipCut = this.slider?.hasAttribute('data-variant-cut');
    const cutId = hasRipCut ? this.slider.getAttribute('data-variant-cut') : null;
    const active = Boolean(cutId) && Number(widthValue) < 225;
    const quantity = active ? this.getRipCutQuantity() : 0;
    const unitPriceCents = active
      ? (parseInt(this.slider.getAttribute('data-variant-cut-price'), 10) || 0)
      : 0;

    window.addonCutActive = active;
    window.addonCutId = active ? cutId : null;
    window.ripCutState = {
      active,
      id: active ? cutId : null,
      quantity,
      unitPriceCents,
      totalPriceCents: unitPriceCents * quantity
    };

    if (!applyPrice) {
      this.schedulePriceSummaryLog('rip cut state', 100);
      return;
    }

    document.dispatchEvent(new CustomEvent('rip-cut:change', {
      detail: window.ripCutState
    }));
    this.updateProductPrice();
  }

  handleRipCutDependencyChange(event) {
    // Length change affects rip cut quantity (per foot) and variant options — not width tier.
    // Quantity is pure arithmetic on the live slider value, so recompute and repaint now.
    this.updateRipCutState(undefined, true);

    const summaryDelay = event?.type === 'length-changed' ? 750 : 350;
    const summaryTrigger = event?.type === 'length-changed' ? 'length change' : 'variant change';
    this.schedulePriceSummaryLog(summaryTrigger, summaryDelay);

    // A width-tier switch dispatches 'variants-block:updated' itself once it
    // finishes (see fetchAndApplyProductHtml). Reacting to that by re-running
    // the width match again is this element responding to its own update —
    // pure redundant work re-verifying a match that just succeeded moments
    // ago, which was firing every width-tier switch twice in a row.
    const isOwnWidthUpdate = event?.type === 'variants-block:updated' &&
      typeof event.detail?.source === 'string' &&
      event.detail.source.startsWith('width-prime-change');
    if (isOwnWidthUpdate) return;

    // Length/variant updates finish after the length slider debounce (~300ms).
    // Re-match the width product variant once those option inputs have settled.
    if (event?.type === 'length-changed' || event?.type === 'theme:variant:change' || event?.type === 'variants-block:updated') {
      const delay = event?.type === 'length-changed' ? 550 : 180;
      this.scheduleWidthVariantSync(delay);
    }
  }

  scheduleWidthVariantSync(delay = 550) {
    clearTimeout(this._widthVariantSyncTimer);
    // See handleSliderChange for why this must be the DOM-reflected tracker,
    // not window.currentWidthProduct (which tracks the target tier, not what
    // the variants markup currently actually shows).
    const fromProduct = window.currentWidthDomProduct;
    window.ScaffPricing.setPending('width', true);
    this._widthVariantSyncTimer = setTimeout(() => {
      const widthValue = parseInt(this.slider?.value, 10);
      if (!Number.isFinite(widthValue) || !this.jsonData?.length) {
        window.ScaffPricing.setPending('width', false);
        return;
      }

      const nearestProduct = this.findNearestWidthProduct(widthValue);
      if (nearestProduct) {
        this.handleVariantChange(nearestProduct, fromProduct);
        this.schedulePriceSummaryLog('width sync after length', 200);
      } else {
        window.ScaffPricing.setPending('width', false);
      }
    }, delay);
  }

  observeProductUpdates() {
    const productSection = this.closest('.product-section') || document.querySelector('.product-section');
    if (!productSection || typeof MutationObserver === 'undefined') return;

    this._productObserver = new MutationObserver(mutations => {
      if (this._updatingRipCutPrice) return;
      const priceChanged = mutations.some(mutation => {
        if (mutation.type === 'attributes') return mutation.target.matches?.('[data-product-price]');
        if (mutation.target.nodeType === 1 && mutation.target.matches?.('[data-product-price]')) return true;
        if (mutation.target.parentElement?.closest?.('[data-product-price]')) return true;
        return Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 && (node.matches?.('[data-product-price]') || node.querySelector?.('[data-product-price]'))
        );
      });
      if (priceChanged) {
        const priceEl = document.querySelector('[data-product-price]');
        const basePriceChanged = mutations.some(mutation =>
          mutation.type === 'attributes' && mutation.attributeName === 'data-base-price'
        );
        const bracket = document.querySelector('addon-bracket-selector');

        if (priceEl) priceEl.removeAttribute('data-rip-cut-applied-cents');
        // Refresh state (quantity/active-ness) without painting yet — whichever
        // branch below owns the actual repaint.
        this.updateRipCutState(undefined, false);

        if (bracket && typeof bracket.updatePrice === 'function') {
          // Bracket owns the final total; only recalculate when the variant base changed.
          if (basePriceChanged) {
            setTimeout(() => bracket.updatePrice(), 0);
          }
        } else {
          this.updateProductPrice();
        }

        this.schedulePriceSummaryLog('variant price from theme', 250);
      }
    });
    this._productObserver.observe(productSection, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-base-price']
    });
  }

  getVariantPriceCents(variant) {
    if (!variant) return NaN;
    if (Number.isFinite(variant.price_cents)) return variant.price_cents;
    if (typeof variant.price === 'number') return variant.price;
    if (typeof variant.price === 'string') {
      const parsed = this.parseDisplayedMoney(variant.price);
      if (Number.isFinite(parsed)) return parsed;
    }
    return NaN;
  }

  syncVariantBasePrice(variant) {
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl || !variant) return;

    const variantCents = this.getVariantPriceCents(variant);
    if (!Number.isFinite(variantCents)) return;

    priceEl.setAttribute('data-base-price', String(variantCents));
    priceEl.removeAttribute('data-rip-cut-applied-cents');
    this.updateProductPrice();
  }

  updateProductPrice() {
    const bracket = document.querySelector('addon-bracket-selector');
    if (bracket && typeof bracket.updatePrice === 'function') {
      bracket.updatePrice();
      this.schedulePriceSummaryLog('rip cut applied', 80);
      return;
    }

    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;

    const rawBase = priceEl.getAttribute('data-base-price');
    if (rawBase === null) return;

    const basePriceCents = parseInt(rawBase, 10) || 0;
    const ripCutPriceCents = window.ripCutState?.active
      ? (window.ripCutState.totalPriceCents || 0)
      : 0;
    const totalCents = window.ScaffPricing.totalCents({ baseCents: basePriceCents, ripCutCents: ripCutPriceCents });
    const currencyFormatter = window.themeVendor?.themeCurrency;
    if (!currencyFormatter || !window.theme) return;

    const formatted = window.theme.settings?.currency_code_enable
      ? currencyFormatter.formatMoney(totalCents, theme.moneyFormat) + ` ${theme.currencyCode}`
      : currencyFormatter.formatMoney(totalCents, theme.moneyFormat);

    this._updatingRipCutPrice = true;
    if (ripCutPriceCents > 0) {
      priceEl.setAttribute('data-rip-cut-applied-cents', ripCutPriceCents);
    } else {
      priceEl.removeAttribute('data-rip-cut-applied-cents');
    }
    priceEl.innerHTML = formatted;
    setTimeout(() => { this._updatingRipCutPrice = false; }, 0);

    this.schedulePriceSummaryLog('rip cut applied', 80);
  }

  parseDisplayedMoney(value) {
    const match = String(value || '').replace(/\s/g, '').match(/-?\d[\d.,]*/);
    if (!match) return NaN;

    let amount = match[0];
    const lastDot = amount.lastIndexOf('.');
    const lastComma = amount.lastIndexOf(',');
    const separatorIndex = Math.max(lastDot, lastComma);

    if (separatorIndex >= 0 && amount.length - separatorIndex - 1 === 2) {
      const whole = amount.slice(0, separatorIndex).replace(/[.,]/g, '');
      const decimals = amount.slice(separatorIndex + 1);
      amount = `${whole}.${decimals}`;
    } else {
      amount = amount.replace(/[.,]/g, '');
    }

    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? Math.round(numericAmount * 100) : NaN;
  }

  debounceVariantChange(product, fromProduct, delay = 650) {
    if (this._variantDebounceTimer) {
      clearTimeout(this._variantDebounceTimer);
    }
    this._variantDebounceTimer = setTimeout(() => {
      this.handleVariantChange(product, fromProduct);
    }, delay);
  }

  updateSelectedValueDisplay(value) {
    if (!this.selectedValueDisplay) return;

    const numericValue = parseFloat(value);
    const inches = (numericValue / 25.4).toFixed(1);

    if (this.exactInput) {
      this.selectedValueDisplay.textContent = `(approx ${inches}in)`;
    } else {
      this.selectedValueDisplay.textContent = `${numericValue}mm (approx ${inches}in)`;
    }
  }

  updateKnob(value) {
    if (!this.knob) return;

    const min = parseFloat(this.slider.min);
    const max = parseFloat(this.slider.max);
    const percent = ((value - min) / (max - min)) * 100;

    this.knob.style.left = `${percent}%`;
    this.knob.textContent = `${value}mm`;
  }

  findNearestWidthProduct(targetWidth) {
    if (!this.jsonData || !Array.isArray(this.jsonData)) return null;

    const target = Math.round(targetWidth);
    // Find all products with width >= target, then pick the smallest one
    let nextProduct = null;
    let minLargerWidth = Infinity;
    for (const product of this.jsonData) {
      const prodWidth = parseInt(product.product_metafields?.width || 0, 10);
      if (prodWidth === 0) continue;
      if (prodWidth >= target && prodWidth < minLargerWidth) {
        minLargerWidth = prodWidth;
        nextProduct = product;
      }
    }
    // If no larger width found, fallback to the largest available
    if (!nextProduct) {
      let maxWidth = -Infinity;
      for (const product of this.jsonData) {
        const prodWidth = parseInt(product.product_metafields?.width || 0, 10);
        if (prodWidth > maxWidth) {
          maxWidth = prodWidth;
          nextProduct = product;
        }
      }
    }
    return nextProduct;
  }

  handleVariantChange(product, fromProduct = null, retryCount = 0, lengthWaitCount = 0) {
    if (!product?.variants) {
      window.ScaffPricing.setPending('width', false);
      return;
    }

    // Length's debounced click (300ms) may still be in flight — wait it out
    // rather than matching against (and fetching HTML for) a stale Size.
    // Tracked separately from the "options not ready" retries below so a
    // slow length resolution can't eat into that budget.
    if (this.isLengthPending() && lengthWaitCount < 10) {
      setTimeout(() => this.handleVariantChange(product, fromProduct, retryCount, lengthWaitCount + 1), 100);
      return;
    }

    const selectedValues = this.getSelectedOptionValues();

    if (!selectedValues.length || selectedValues.includes(null)) {
      if (retryCount < 6) {
        setTimeout(() => this.handleVariantChange(product, fromProduct, retryCount + 1, lengthWaitCount), 150);
        return;
      }
      console.warn('[Width Prime] Options not ready for variant match:', selectedValues);
      window.ScaffPricing.setPending('width', false);
      return;
    }

    // findVariantMatch first tries an exact (normalized) match, then falls back
    // to mapping option "slots" from fromProduct onto product — this is the
    // single source of truth for variant matching, shared with the immediate
    // price-sync path in handleSliderChange, so the two can never disagree.
    const matchingVariant = this.findVariantMatch(product, fromProduct);

    if (matchingVariant) {
      window.currentMatchingVariant = matchingVariant;
      this.applyVariantSelection(matchingVariant, product);
      this.schedulePriceSummaryLog('width tier variant matched', 400);
      window.ScaffPricing.setPending('width', false);
      return;
    }

    window.currentMatchingVariant = null;
    console.warn('[Width Prime] No variant match (even after positional mapping) for width product.', {
      selectedValues,
      width: product.product_metafields?.width
    });
    window.ScaffPricing.setPending('width', false);
  }

  applyVariantSelection(matchingVariant, product, updateSource = 'width-prime-change') {
    const idInput = document.querySelector("input[type='hidden'][name='id']");
    const variantChanged = idInput && String(idInput.value) !== String(matchingVariant.id);

    if (idInput) {
      idInput.value = matchingVariant.id;
      if (variantChanged && typeof idInput.dispatchEvent === 'function') {
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } else {
      console.warn("[Width Prime] Could not find input[type='hidden'][name='id']");
    }

    // Width tier changes select a different variant price. Sync data-base-price
    // from the matched JSON variant so addon/rip-cut layers recalculate live.
    this.updateRipCutState(parseInt(this.slider?.value, 10), false);
    this.syncVariantBasePrice(matchingVariant);

    if (!matchingVariant.url || typeof matchingVariant.url !== 'string' || !window.location) return;

    window.history.replaceState({}, '', matchingVariant.url);
    this.fetchAndApplyProductHtml(matchingVariant.url, updateSource, product);
  }

  fetchAndApplyProductHtml(url, updateSource = 'width-prime-change', product = null) {
    const useHtml = (html) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const newVariantsBlock = tempDiv.querySelector('.product__block__variants');
      const currentVariantsBlock = document.querySelector('.product__block__variants');
      if (newVariantsBlock && currentVariantsBlock) {
        // The DOM now genuinely reflects `product`'s option values, whether
        // we just swapped the markup in or it already matched — this is the
        // only place that tracker should move forward.
        if (product) window.currentWidthDomProduct = product;

        if (newVariantsBlock.innerHTML !== currentVariantsBlock.innerHTML) {
          currentVariantsBlock.replaceWith(newVariantsBlock);

          document.dispatchEvent(new CustomEvent('variants-block:updated', {
            detail: { source: updateSource },
            bubbles: true
          }));

          const productSection = document.querySelector('[data-section-id][data-section-type="product"]') ||
            document.querySelector('.product-page[data-section-id]');
          if (productSection) {
            const sectionId = productSection.getAttribute('data-section-id');
            document.dispatchEvent(new CustomEvent('shopify:section:load', {
              detail: { sectionId },
              bubbles: true
            }));
          }
        }
      } else {
        console.warn('[Width Prime] Could not find new or current variants block.');
      }

      const newDescriptionBlock = tempDiv.querySelector('.product__block__description');
      const currentDescriptionBlock = document.querySelector('.product__block__description');
      if (newDescriptionBlock && currentDescriptionBlock) {
        const newAccordionInners = newDescriptionBlock.querySelectorAll('.accordion-content__inner');
        const currentAccordionInners = currentDescriptionBlock.querySelectorAll('.accordion-content__inner');
        if (newAccordionInners.length === currentAccordionInners.length) {
          newAccordionInners.forEach((newInner, idx) => {
            const currentInner = currentAccordionInners[idx];
            if (currentInner && newInner) {
              currentInner.innerHTML = newInner.innerHTML;
            }
          });
        } else {
          console.warn('[Width Prime] Accordion inner count mismatch, skipping update.');
        }
      }
    };

    if (OptionWidthPrimeRange.productHtmlCache[url]) {
      useHtml(OptionWidthPrimeRange.productHtmlCache[url]);
      return;
    }

    fetch(url)
      .then(res => res.text())
      .then(html => {
        OptionWidthPrimeRange.productHtmlCache[url] = html;
        useHtml(html);
      })
      .catch(err => {
        console.error('[Width Prime] Failed to fetch or replace product blocks:', err);
      });
  }


}

customElements.define('option-width-prime-range', OptionWidthPrimeRange);
